import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { downloadPdf } from "@/lib/api";
import { LEVELS, yearLabel } from "@/components/shared";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Plus, Trash2, Loader2, GraduationCap, FolderOpen, Download, FileText, CheckCircle2, Copy, Upload, Search, RefreshCw,
} from "lucide-react";
import ProofCard from "@/components/ProofCard";
import CareerDocCard from "@/components/CareerDocCard";
import PfmpPortfolioCard from "@/components/PfmpPortfolioCard";
import { input, btn, btnGhost, copyText } from "./adminShared";
import StreakLeaderboard from "./StreakLeaderboard";

function parseStudentLines(text, year, order = "first-last") {
  return text
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean)
    .map((line) => {
      let first = "";
      let last = "";
      if (line.includes(",")) {
        const [a, b] = line.split(",");
        last = (a || "").trim();
        first = (b || "").trim();
      } else {
        const parts = line.split(/[\s\t]+/).filter(Boolean);
        if (order === "last-first") {
          first = parts.pop() || "";
          last = parts.join(" ");
        } else {
          first = parts.shift() || "";
          last = parts.join(" ");
        }
      }
      return { first_name: first, last_name: last, year };
    })
    .filter((r) => r.first_name && r.last_name);
}

export default function StudentsTab() {
  const [students, setStudents] = useState(null);
  const [form, setForm] = useState({ first_name: "", last_name: "", year: 1 });
  const [yearFilter, setYearFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [proofsFor, setProofsFor] = useState(null);
  const [studentProofs, setStudentProofs] = useState([]);
  const [careerDocs, setCareerDocs] = useState(null);
  const [pfmpPortfolios, setPfmpPortfolios] = useState(null);
  const [exportingId, setExportingId] = useState(null);
  const [gridBusy, setGridBusy] = useState(null);
  const [codesBusy, setCodesBusy] = useState(false);
  const [newByStudent, setNewByStudent] = useState({});
  // bulk import
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkText, setBulkText] = useState("");
  const [bulkYear, setBulkYear] = useState(1);
  const [bulkOrder, setBulkOrder] = useState("first-last");
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);

  const load = () => api.get("/admin/students").then(({ data }) => setStudents(data));
  const loadNotifs = () => api.get("/admin/notifications").then(({ data }) => {
    const map = {};
    (data.items || []).forEach((it) => { map[it.student_id] = it.count; });
    setNewByStudent(map);
  }).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); loadNotifs(); }, []);

  const viewProofs = async (s) => {
    setProofsFor(s);
    setStudentProofs(null);
    setCareerDocs(null);
    setPfmpPortfolios(null);
    const { data } = await api.get(`/admin/students/${s.id}/proofs`);
    setStudentProofs(data);
    const { data: cd } = await api.get(`/admin/students/${s.id}/career-docs`);
    setCareerDocs(cd);
    const { data: pp } = await api.get(`/admin/students/${s.id}/pfmp-portfolios`);
    setPfmpPortfolios(pp);
  };

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/admin/students", {
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        year: Number(form.year),
      });
      toast.success(`Élève ajouté ✓ — code de connexion : ${data.pin}`);
      setForm({ first_name: "", last_name: "", year: form.year });
      setOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally { setBusy(false); }
  };

  const submitBulk = async () => {
    const rows = parseStudentLines(bulkText, Number(bulkYear), bulkOrder);
    if (rows.length === 0) { toast.error("Aucun élève détecté. Un élève par ligne."); return; }
    setBulkBusy(true);
    try {
      const { data } = await api.post("/admin/students/bulk", { students: rows });
      setBulkResult(data);
      toast.success(`${data.created_count} élève(s) ajouté(s)`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally { setBulkBusy(false); }
  };

  const remove = async (id) => {
    await api.delete(`/admin/students/${id}`);
    toast.success("Élève supprimé");
    load();
  };

  const [regenBusy, setRegenBusy] = useState(null);
  const regeneratePin = async (s) => {
    setRegenBusy(s.id);
    try {
      const { data } = await api.post(`/admin/students/${s.id}/regenerate-pin`);
      toast.success(`Nouveau code pour ${s.name} : ${data.pin}`);
      load();
    } catch {
      toast.error("Échec de la régénération du code.");
    } finally { setRegenBusy(null); }
  };

  const downloadCodes = async () => {
    setCodesBusy(true);
    try {
      await downloadPdf("/admin/students/credentials/pdf", "liste_classe_merryzone.pdf");
      toast.success("Liste de la classe téléchargée ✓");
    } catch {
      toast.error("Échec du téléchargement");
    } finally { setCodesBusy(false); }
  };

  const exportPdf = async (s) => {
    setExportingId(s.id);
    try {
      await downloadPdf(`/admin/students/${s.id}/portfolio/pdf`, `portfolio_${s.name.replace(/\s+/g, "_")}.pdf`);
      toast.success(`Portfolio de ${s.name} exporté ✓`);
    } catch (e) {
      toast.error("Échec de l'export PDF.");
    } finally {
      setExportingId(null);
    }
  };

  const [exportingPfmpId, setExportingPfmpId] = useState(null);
  const exportPfmpPortfolioPdf = async (studentId, studentName, pf) => {
    setExportingPfmpId(pf.id);
    try {
      await downloadPdf(`/admin/students/${studentId}/pfmp-portfolio/${pf.id}/pdf`, `portfolio_${pf.title.replace(/\s+/g, "_")}_${studentName.replace(/\s+/g, "_")}.pdf`);
      toast.success(`Portfolio ${pf.title} exporté ✓`);
    } catch {
      toast.error("Échec de l'export PDF.");
    } finally {
      setExportingPfmpId(null);
    }
  };

  const exportGrid = async (s, bloc) => {
    const key = `${s.id}-${bloc}`;
    setGridBusy(key);
    try {
      await downloadPdf(`/admin/students/${s.id}/evaluation-grid/${bloc}/pdf`, `grille_bloc${bloc}_${s.name.replace(/\s+/g, "_")}.pdf`);
      toast.success(`Grille Bloc ${bloc} de ${s.name} exportée ✓`);
    } catch (e) {
      toast.error("Échec de l'export de la grille.");
    } finally {
      setGridBusy(null);
    }
  };

  const [classExportBusy, setClassExportBusy] = useState(null);
  const exportClassZip = async (kind, year) => {
    const key = `${kind}-${year}`;
    setClassExportBusy(key);
    try {
      const path = kind === "grids" ? "/admin/class-export/ccf-grids.zip" : "/admin/class-export/career-docs.zip";
      const filename = kind === "grids" ? `grilles_ccf_${year}e_annee.zip` : `cv_lettres_valides_${year}e_annee.zip`;
      await downloadPdf(`${path}?year=${year}`, filename);
      toast.success("Export groupé téléchargé ✓");
    } catch (e) {
      toast.error(e.response?.status === 404 ? "Rien à exporter pour cette année." : "Échec de l'export groupé.");
    } finally { setClassExportBusy(null); }
  };

  if (!students) return <div className="flex justify-center py-20"><Loader2 className="h-7 w-7 text-orange-400 animate-spin" /></div>;

  const sortedStudents = [...students].sort((a, b) => (newByStudent[b.id] || 0) - (newByStudent[a.id] || 0));
  const norm = (t) => (t || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  const filteredStudents = sortedStudents.filter((s) =>
    (yearFilter === "all" || s.year === yearFilter) && (!search.trim() || norm(s.name).includes(norm(search))));
  const countY1 = students.filter((s) => s.year === 1).length;
  const countY2 = students.filter((s) => s.year === 2).length;
  const totalToReview = Object.values(newByStudent).reduce((acc, n) => acc + (n || 0), 0);
  const parsedBulkCount = parseStudentLines(bulkText, Number(bulkYear), bulkOrder).length;

  return (
    <div>
      <div className="flex items-center justify-between gap-3 mb-5 flex-wrap">
        <div>
          <p className="text-slate-500 font-medium"><span className="font-bold text-slate-800">{students.length}</span> élève{students.length > 1 ? "s" : ""} inscrit{students.length > 1 ? "s" : ""}</p>
          {totalToReview > 0 && <p className="text-xs text-orange-600 font-bold mt-0.5">Les élèves avec des preuves à évaluer apparaissent en premier.</p>}
        </div>
        <div className="flex items-center gap-2">
          {/* Download credentials PDF */}
          <button onClick={downloadCodes} disabled={codesBusy || students.length === 0} className={btnGhost + " px-4 py-2.5 rounded-full border border-slate-200 disabled:opacity-50"} data-testid="download-codes-btn">
            {codesBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />} Liste de la classe (PDF)
          </button>
          {/* Bulk import */}
          <Dialog open={bulkOpen} onOpenChange={(o) => { setBulkOpen(o); if (!o) { setBulkResult(null); setBulkText(""); } }}>
            <DialogTrigger asChild>
              <button className={btnGhost + " px-4 py-2.5 rounded-full border border-slate-200"} data-testid="import-students-btn"><Upload className="h-4 w-4" /> Importer une classe</button>
            </DialogTrigger>
            <DialogContent className="rounded-3xl max-w-lg max-h-[88vh] overflow-y-auto">
              <DialogHeader><DialogTitle className="font-display text-xl">Importer une classe</DialogTitle></DialogHeader>
              <DialogDescription className="sr-only">Coller la liste des élèves, un par ligne, pour les créer en une fois.</DialogDescription>
              {!bulkResult ? (
                <div className="space-y-3 mt-2">
                  <p className="text-sm text-slate-500 font-medium">Colle ta liste : <strong>un élève par ligne</strong>. Choisis l'ordre des noms ci-dessous (« Nom Prénom » pour un copier-coller Pronote). Le format « Nom, Prénom » avec virgule est aussi reconnu.</p>
                  <textarea
                    className={input + " min-h-[160px] font-mono text-sm"}
                    placeholder={"ATTIA Shady\nBEDDIAF Mohammed\nSAINT-AUBERT Shauna"}
                    value={bulkText}
                    onChange={(e) => setBulkText(e.target.value)}
                    data-testid="bulk-textarea"
                  />
                  <div className="flex items-center gap-3 flex-wrap">
                    <label className="text-sm font-bold text-slate-700">Ordre :</label>
                    <select className={input + " max-w-[210px]"} value={bulkOrder} onChange={(e) => setBulkOrder(e.target.value)} data-testid="bulk-order">
                      <option value="first-last">Prénom Nom</option>
                      <option value="last-first">Nom Prénom (Pronote)</option>
                    </select>
                    <label className="text-sm font-bold text-slate-700">Année :</label>
                    <select className={input + " max-w-[150px]"} value={bulkYear} onChange={(e) => setBulkYear(e.target.value)} data-testid="bulk-year">
                      <option value={1}>1re année</option>
                      <option value={2}>2e année</option>
                    </select>
                    <span className="text-sm text-slate-400 font-medium ml-auto">{parsedBulkCount} élève(s) détecté(s)</span>
                  </div>
                  <DialogFooter className="pt-2">
                    <button type="button" disabled={bulkBusy || parsedBulkCount === 0} className={btn} onClick={submitBulk} data-testid="bulk-submit-btn">
                      {bulkBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Importer {parsedBulkCount > 0 ? `(${parsedBulkCount})` : ""}
                    </button>
                  </DialogFooter>
                </div>
              ) : (
                <div className="space-y-3 mt-2" data-testid="bulk-result">
                  <div className="rounded-2xl bg-green-50 p-3 text-green-700 font-bold text-sm flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4" /> {bulkResult.created_count} élève(s) créé(s){bulkResult.skipped_count > 0 ? ` · ${bulkResult.skipped_count} ignoré(s) (déjà existants)` : ""}
                  </div>
                  {bulkResult.created.length > 0 && (
                    <>
                      <div className="flex items-center justify-between">
                        <p className="text-sm font-bold text-slate-700">Élèves ajoutés</p>
                        <button className={btnGhost} onClick={() => copyText(bulkResult.created.map((c) => `${c.name} — code ${c.pin}`).join("\n"))}><Copy className="h-4 w-4" /> Copier la liste</button>
                      </div>
                      <div className="rounded-2xl border border-slate-100 divide-y divide-slate-50 max-h-56 overflow-y-auto">
                        {bulkResult.created.map((c) => (
                          <div key={c.name} className="flex items-center justify-between px-3 py-2">
                            <span className="font-medium text-slate-700">{c.name}</span>
                            <span className="flex items-center gap-2">
                              <span className="text-xs font-mono font-bold text-orange-600 bg-orange-50 px-2 py-0.5 rounded-full">{c.pin}</span>
                              <span className="text-xs text-slate-400 font-semibold">{c.year === 1 ? "1re année" : "2e année"}</span>
                            </span>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                  {bulkResult.skipped.length > 0 && (
                    <p className="text-xs text-slate-400 font-medium">Ignorés : {bulkResult.skipped.map((s) => s.name).join(", ")}</p>
                  )}
                  <DialogFooter className="pt-2">
                    <button type="button" className={btn} onClick={() => { setBulkResult(null); setBulkText(""); }}>Importer une autre liste</button>
                  </DialogFooter>
                </div>
              )}
            </DialogContent>
          </Dialog>

          {/* Single add */}
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <button className={btn} data-testid="add-student-btn"><Plus className="h-4 w-4" /> Ajouter un élève</button>
            </DialogTrigger>
            <DialogContent className="rounded-3xl">
              <DialogHeader><DialogTitle className="font-display text-xl">Nouvel élève</DialogTitle></DialogHeader>
              <DialogDescription className="sr-only">Ajouter un élève avec son prénom, son nom et son année.</DialogDescription>
              <form onSubmit={create} className="space-y-3 mt-2">
                <div className="grid grid-cols-2 gap-3">
                  <input className={input} placeholder="Prénom" required value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} data-testid="student-first-name" />
                  <input className={input} placeholder="Nom" required value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} data-testid="student-last-name" />
                </div>
                <select className={input} value={form.year} onChange={(e) => setForm({ ...form, year: e.target.value })} data-testid="student-year">
                  <option value={1}>1re année</option>
                  <option value={2}>2e année</option>
                </select>
                <p className="text-xs text-slate-400 font-medium">L'élève se connectera avec son prénom et son nom.</p>
                <DialogFooter className="pt-2">
                  <button type="submit" disabled={busy} className={btn} data-testid="save-student-btn">{busy ? "…" : "Créer l'élève"}</button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {students.length > 0 && (
        <div className="rounded-2xl bg-slate-50 border border-slate-100 p-4 mb-5 flex flex-col sm:flex-row sm:items-center gap-3" data-testid="class-export-panel">
          <p className="text-xs font-extrabold uppercase text-slate-400 shrink-0">Export groupé</p>
          <div className="flex items-center gap-2 flex-wrap">
            {[1, 2].map((y) => (
              <button key={`grids-${y}`} onClick={() => exportClassZip("grids", y)} disabled={classExportBusy === `grids-${y}`}
                data-testid={`export-class-grids-${y}`}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-white border border-slate-200 hover:border-orange-300 disabled:opacity-50 text-slate-700 text-xs font-bold">
                {classExportBusy === `grids-${y}` ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />} Grilles CCF {y}re/e année (ZIP)
              </button>
            ))}
            {[1, 2].map((y) => (
              <button key={`career-${y}`} onClick={() => exportClassZip("career", y)} disabled={classExportBusy === `career-${y}`}
                data-testid={`export-class-career-${y}`}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-white border border-slate-200 hover:border-orange-300 disabled:opacity-50 text-slate-700 text-xs font-bold">
                {classExportBusy === `career-${y}` ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />} CV/lettres validés {y}re/e année (ZIP)
              </button>
            ))}
          </div>
        </div>
      )}

      <StreakLeaderboard />

      {students.length === 0 ? (
        <div className="rounded-3xl bg-white border border-slate-100 shadow-sm p-12 text-center">
          <GraduationCap className="h-10 w-10 text-slate-300 mx-auto" />
          <p className="text-slate-500 font-medium mt-3">Aucun élève pour l'instant. Ajoute ta classe !</p>
        </div>
      ) : (
        <>
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-4">
            <div className="flex items-center gap-1 bg-white border border-slate-100 rounded-full p-1 shadow-sm w-fit">
              {[["all", `Tous (${students.length})`], [1, `1re année (${countY1})`], [2, `2e année (${countY2})`]].map(([v, label]) => (
                <button key={v} onClick={() => setYearFilter(v)} data-testid={`year-filter-${v}`}
                  className={"px-4 py-1.5 rounded-full text-sm font-bold transition-colors " + (yearFilter === v ? "bg-orange-500 text-white" : "text-slate-500 hover:text-slate-800")}>
                  {label}
                </button>
              ))}
            </div>
            <div className="relative flex-1 sm:max-w-xs">
              <Search className="h-4 w-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
              <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Rechercher un élève…"
                className={input + " pl-10 rounded-full"} data-testid="student-search-input" />
            </div>
          </div>
          <div className="space-y-3">
          {filteredStudents.length === 0 ? (
            <div className="rounded-3xl bg-white border border-slate-100 shadow-sm p-10 text-center text-slate-400 font-medium" data-testid="no-students-match">
              Aucun élève ne correspond à ce filtre.
            </div>
          ) : filteredStudents.map((s) => (
            <div key={s.id} className="rounded-3xl bg-white border border-slate-100 shadow-sm p-5 flex flex-col sm:flex-row sm:items-center gap-4" data-testid={`student-row-${s.id}`}>
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <div className="h-11 w-11 rounded-2xl bg-orange-100 text-orange-600 flex items-center justify-center font-display font-semibold shrink-0">
                  {s.name.split(" ").map((p) => p[0]).slice(0, 2).join("")}
                </div>
                <div className="min-w-0">
                  <p className="font-bold text-slate-800">{s.name}</p>
                  <p className="text-sm text-slate-400 font-medium mt-0.5 flex items-center gap-1.5">
                    {yearLabel(s.year)}
                    <span className="text-[10px] font-mono font-bold text-orange-600 bg-orange-50 px-1.5 py-0.5 rounded-full" data-testid={`student-pin-${s.id}`} title="Code de connexion">
                      Code {s.pin}
                    </span>
                    <button
                      onClick={() => regeneratePin(s)}
                      disabled={regenBusy === s.id}
                      data-testid={`regenerate-pin-${s.id}`}
                      title="Régénérer le code"
                      className="inline-flex items-center justify-center h-5 w-5 rounded-full text-slate-400 hover:text-orange-600 hover:bg-orange-50 disabled:opacity-50 transition-colors"
                    >
                      {regenBusy === s.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
                    </button>
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-1.5 flex-wrap">
                {s.portfolio.map((c) => (
                  <span key={c.code} className="text-[10px] font-extrabold px-2 py-1 rounded-lg bg-slate-50 text-slate-500" title={`${c.short}: ${LEVELS[c.level - 1].label}`}>
                    {c.code}<span className="text-orange-500">·{LEVELS[c.level - 1].code}</span>
                  </span>
                ))}
              </div>
              <div className="flex items-center gap-3 sm:pl-4 sm:border-l border-slate-100">
                <div className="text-center">
                  <p className="text-[10px] font-extrabold uppercase text-slate-400">Moyen</p>
                  <p className="font-display font-semibold text-orange-600">{s.avg_level}</p>
                </div>
                <button onClick={() => viewProofs(s)} data-testid={`view-proofs-${s.id}`} className="relative inline-flex items-center gap-1.5 px-3 h-9 rounded-full bg-sky-50 text-sky-600 hover:bg-sky-100 text-sm font-bold">
                  <FolderOpen className="h-4 w-4" /> Preuves
                  {newByStudent[s.id] > 0 && (
                    <span data-testid={`new-proofs-badge-${s.id}`} className="absolute -top-1.5 -right-1.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-extrabold">
                      {newByStudent[s.id]}
                    </span>
                  )}
                </button>
                <button onClick={() => exportPdf(s)} disabled={exportingId === s.id} data-testid={`export-pdf-${s.id}`} className="inline-flex items-center gap-1.5 px-3 h-9 rounded-full bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-60 text-sm font-bold">
                  {exportingId === s.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} PDF
                </button>
                <button onClick={() => remove(s.id)} data-testid={`delete-student-${s.id}`} className="h-9 w-9 rounded-full bg-red-50 text-red-500 hover:bg-red-100 flex items-center justify-center">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
          </div>
        </>
      )}
      <Dialog open={!!proofsFor} onOpenChange={(o) => { if (!o) { setProofsFor(null); setStudentProofs([]); setCareerDocs(null); setPfmpPortfolios(null); } }}>
        <DialogContent className="rounded-3xl max-w-2xl max-h-[88vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">Preuves de {proofsFor?.name}</DialogTitle>
            <DialogDescription>Preuves déposées, documents de recherche de stage et évaluation du professeur.</DialogDescription>
          </DialogHeader>
          {proofsFor && (
            <div className="flex items-center gap-2 flex-wrap -mt-1 mb-2">
              <button onClick={() => exportGrid(proofsFor, "2")} disabled={gridBusy === `${proofsFor.id}-2`}
                data-testid={`export-grid-bloc2-${proofsFor.id}`}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-slate-100 hover:bg-slate-200 disabled:opacity-60 text-slate-700 text-xs font-bold">
                {gridBusy === `${proofsFor.id}-2` ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />} Grille Bloc 2 (PDF)
              </button>
              <button onClick={() => exportGrid(proofsFor, "3")} disabled={gridBusy === `${proofsFor.id}-3`}
                data-testid={`export-grid-bloc3-${proofsFor.id}`}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-slate-100 hover:bg-slate-200 disabled:opacity-60 text-slate-700 text-xs font-bold">
                {gridBusy === `${proofsFor.id}-3` ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />} Grille Bloc 3 (PDF)
              </button>
            </div>
          )}
          {studentProofs === null ? (
            <div className="flex justify-center py-12"><Loader2 className="h-7 w-7 text-orange-400 animate-spin" /></div>
          ) : studentProofs.length === 0 ? (
            <p className="text-slate-500 font-medium py-8 text-center">Aucune preuve déposée pour l'instant.</p>
          ) : (
            <div className="space-y-3 mt-2">
              {studentProofs.map((p) => (
                <div key={p.id}>
                  <p className="text-[11px] font-extrabold uppercase tracking-wide text-slate-400 mb-1">{p.bloc} · {p.seance_title}</p>
                  <ProofCard proof={p} reviewMode onChanged={() => { viewProofs(proofsFor); loadNotifs(); }} />
                </div>
              ))}
            </div>
          )}

          {careerDocs && (
            <div className="mt-6 pt-5 border-t border-slate-100" data-testid="admin-career-section">
              <h3 className="font-display text-lg font-semibold text-slate-900 mb-3">Recherche de stage</h3>
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <p className="text-[11px] font-extrabold uppercase tracking-wide text-slate-400 mb-1.5">CV</p>
                  {careerDocs.cv ? <CareerDocCard doc={careerDocs.cv} reviewMode onChanged={() => viewProofs(proofsFor)} /> : <p className="text-sm text-slate-400 font-medium">Pas encore déposé.</p>}
                </div>
                <div>
                  <p className="text-[11px] font-extrabold uppercase tracking-wide text-slate-400 mb-1.5">Lettre de motivation</p>
                  {careerDocs.cover_letter ? <CareerDocCard doc={careerDocs.cover_letter} reviewMode onChanged={() => viewProofs(proofsFor)} /> : <p className="text-sm text-slate-400 font-medium">Pas encore déposée.</p>}
                </div>
              </div>
              <div className="mt-4 space-y-3">
                {(careerDocs.pfmps || []).map((pf) => {
                  const proofs = careerDocs.pfmp_proofs.filter((p) => p.pfmp_id === pf.id);
                  return (
                    <div key={pf.id}>
                      <p className="text-[11px] font-extrabold uppercase tracking-wide text-slate-400 mb-1.5">Preuves de recherche — {pf.title}</p>
                      {proofs.length === 0 ? (
                        <p className="text-sm text-slate-400 font-medium">Aucune preuve déposée.</p>
                      ) : (
                        <div className="space-y-2">
                          {proofs.map((p) => <CareerDocCard key={p.id} doc={p} reviewMode onChanged={() => viewProofs(proofsFor)} />)}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {pfmpPortfolios && (
            <div className="mt-6 pt-5 border-t border-slate-100" data-testid="admin-pfmp-portfolio-section">
              <h3 className="font-display text-lg font-semibold text-slate-900 mb-3">Portfolio PFMP</h3>
              <div className="space-y-3">
                {(pfmpPortfolios.pfmps || []).map((pf) => {
                  const portfolio = pfmpPortfolios.portfolios?.[pf.id];
                  return (
                    <div key={pf.id}>
                      {portfolio ? (
                        <>
                          <div className="flex justify-end mb-1.5">
                            <button onClick={() => exportPfmpPortfolioPdf(proofsFor.id, proofsFor.name, pf)} disabled={exportingPfmpId === pf.id}
                              data-testid={`export-pfmp-portfolio-pdf-${pf.id}`}
                              className="inline-flex items-center gap-1.5 text-xs font-bold text-slate-500 hover:text-orange-600 disabled:opacity-50">
                              {exportingPfmpId === pf.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />} Télécharger en PDF
                            </button>
                          </div>
                          <PfmpPortfolioCard portfolio={portfolio} reviewMode onChanged={() => viewProofs(proofsFor)} />
                        </>
                      ) : (
                        <div className="rounded-2xl bg-slate-50 border border-slate-100 p-4">
                          <p className="font-bold text-slate-600 text-sm">{pf.title}</p>
                          <p className="text-sm text-slate-400 font-medium mt-0.5">Pas encore rempli.</p>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
